import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { VIEW, DELETE } from "const"
import { CcRequirement } from "server/models"
import {
    PRM_CC_REQUIREMENT,
    StoreType,
    setCcRequirementSelected,
    addModal,
} from "client/store"
import { hasProjectAccess } from "client/utils"
import {
    Link,
    DateFormatter,
    TableColumn,
    TablePopover,
    TablePopoverData,
    UserIcon,
    NotAssigned,
    Empty,
} from "client/components"

const Columns = () => {
    const baseRoute = useSelector((state: StoreType) => state.ccRequirement.baseRoute)
    const selected = useSelector((state: StoreType) => state.ccRequirement.selected)
    const onSelectRequirement = useSelector((state: StoreType) => state.ccRequirement.onSelect)

    const typeList = useSelector((state: StoreType) => state.ccRequirementType.typeList)
    const priorityList = useSelector((state: StoreType) => state.ccRequirementPriority.priorityList)
    const statusList = useSelector((state: StoreType) => state.ccRequirementStatus.statusList)
    const methodList = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodList)
    const methodIds = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodIds)
    const complianceList = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceList)
    const complianceIds = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceIds)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const deleteAccess = hasProjectAccess(PRM_CC_REQUIREMENT, DELETE)

    const requirementLink = (requirement: CcRequirement, linkContent: any) => {
        const href = `${baseRoute}/${VIEW}/${requirement.id}`
        return (
            <Link
                href={href}
                onClick={() => dispatch(addModal({ url: href }))}
            >
                {linkContent}
            </Link>
        )
    }

    const selectRequirement = (requirement: CcRequirement) => {
        if (!onSelectRequirement) return
        let selectedCopy
        if (selected && selected.includes(requirement.id)) {
            selectedCopy = selected.filter((id) => (id !== requirement.id))
        } else {
            selectedCopy = selected ? selected.slice() : []
            selectedCopy.push(requirement.id)
        }
        dispatch(setCcRequirementSelected(selectedCopy))
    }

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
            onClick: selectRequirement,
        },
        {
            field: "uniqueId",
            header: lang.uniqueId,
            sortable: true,
            collapsible: true,
            cellMinWidth: "150px",
            cellMaxWidth: "250px",
            formatter: (requirement: CcRequirement) => (
                onSelectRequirement ? requirement.uniqueId : requirementLink(requirement, requirement.uniqueId)
            ),
            onClick: selectRequirement,
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            formatter: (requirement: CcRequirement) => (
                onSelectRequirement ? requirement.name : requirementLink(requirement, requirement.name)
            ),
            onClick: selectRequirement,
        },
        {
            field: "creator",
            header: lang.createdBy,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (requirement: CcRequirement) => {
                const creator = userList[requirement.creator]
                return creator ? <UserIcon user={creator} size="sm" /> : <NotAssigned />
            },
            onClick: selectRequirement,
        },
        {
            field: "typeId",
            header: lang.type,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (requirement: CcRequirement) => {
                const type = typeList[requirement.typeId]
                return type ? type.name : <NotAssigned />
            },
            onClick: selectRequirement,
        },
        {
            field: "priorityId",
            header: lang.priority,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (requirement: CcRequirement) => {
                const priority = priorityList[requirement.priorityId]
                return priority ? priority.name : <NotAssigned />
            },
            onClick: selectRequirement,
        },
        {
            field: "statusId",
            header: lang.status,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (requirement: CcRequirement) => {
                const status = statusList[requirement.statusId]
                return status ? status.name : <NotAssigned />
            },
            onClick: selectRequirement,
        },
        {
            field: "reviewMethods",
            header: lang.reviewMethods,
            cellMinWidth: "200px",
            formatter: (requirement: CcRequirement) => {
                if (!requirement.reviewMethodsParsed || !Object.keys(requirement.reviewMethodsParsed).length) return <Empty />

                const dataList: TablePopoverData[] = []
                methodIds.forEach((methodId) => {
                    const method = methodList[methodId]
                    if (method && requirement.reviewMethodsParsed[methodId]) {
                        dataList.push({
                            id: `${requirement.id}_${methodId}`,
                            title: method.name,
                        })
                    }
                })

                return (
                    <TablePopover
                        id={`reviewMethods${requirement.id}`}
                        dataList={dataList}
                        placement="right"
                        title={lang.reviewMethods}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
            onClick: selectRequirement,
        },
        {
            field: "smartCompliance",
            header: lang.smartCompliance,
            cellMinWidth: "200px",
            formatter: (requirement: CcRequirement) => {
                if (!requirement.smartComplianceParsed || !Object.keys(requirement.smartComplianceParsed).length) return <Empty />

                const dataList: TablePopoverData[] = []
                complianceIds.forEach((complianceId) => {
                    const compliance = complianceList[complianceId]
                    if (compliance && requirement.smartComplianceParsed[complianceId]) {
                        dataList.push({
                            id: `${requirement.id}_${complianceId}`,
                            title: compliance.name,
                        })
                    }
                })

                return (
                    <TablePopover
                        id={`smartCompliance${requirement.id}`}
                        dataList={dataList}
                        placement="right"
                        title={lang.smartCompliance}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
            onClick: selectRequirement,
        },
        {
            field: "createdAt",
            header: lang.created,
            sortable: true,
            formatter: (requirement: CcRequirement) => <DateFormatter date={requirement.createdAt} />,
            onClick: selectRequirement,
        },
        {
            field: "delete",
            centerAlign: true,
            deleteAccess: (requirement: CcRequirement) => deleteAccess || requirement.creator === userData.userId,
        },
    ]

    return columns
}
export default Columns
